import { c as _c } from "react/compiler-runtime";
import { Dialog, Stack, type DialogProps } from '@mui/material';
import classnames from 'classnames';
import React, { type PropsWithChildren } from 'react';
import { Loader } from '@/components/shared/loader';
import { PanelWrapper, type PanelWrapperProps } from '../panels/panelWrapper';
export interface MainDialogProps extends PanelWrapperProps {
  isOpen: boolean;
  isLoading: boolean;
  disableGutters?: boolean;
  /** Render as a full-viewport overlay instead of a centered modal. */
  fullScreen?: boolean;
  /**
   * Pass 0 for dialogs that replace one another (e.g. wizard-like step screens): each screen is its
   * own Dialog, so the default fade replays from transparent on every swap and reads as a flash.
   * An `{ enter, exit }` object animates the two directions independently.
   */
  transitionDuration?: DialogProps['transitionDuration'];
  /**
   * Fires after the exit transition finishes. Unmounting on close skips the exit fade entirely, so a
   * caller that wants one must flip `isOpen` to false and unmount from this callback instead.
   */
  onExited?: () => void;
  /**
   * Makes the panel body the scroll region when content exceeds the dialog's height cap: the header
   * and footer stay pinned and the scrollbar is clipped inside the corner radius. Off by default —
   * without it, overflowing content scrolls the whole paper, header and footer included.
   */
  scrollableBody?: boolean;
}
export function MainDialog(props) {
  const $ = _c(40);
  const {
    isLoading,
    isOpen,
    header,
    footer,
    sx,
    disableGutters: t0,
    fullScreen: t1,
    transitionDuration,
    onExited,
    scrollableBody: t2
  } = props;
  const disableGutters = t0 === undefined ? false : t0;
  const fullScreen = t1 === undefined ? false : t1;
  const scrollableBody = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== header) {
    t3 = () => {
      header?.onClose?.();
    };
    $[0] = header;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onExited) {
    t4 = {
      onExited
    };
    $[2] = onExited;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== fullScreen || $[5] !== scrollableBody || $[6] !== sx) {
    t5 = {
      sx: fullScreen ? {
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        borderRadius: "0px",
        ...sx
      } : {
        width: "40%",
        maxWidth: "840px",
        minWidth: "600px",
        height: "max-content",
        minHeight: "300px",
        maxHeight: "90vh",
        borderRadius: "16px",
        ...(scrollableBody && {
          overflow: "hidden"
        }),
        ...sx
      }
    };
    $[4] = fullScreen;
    $[5] = scrollableBody;
    $[6] = sx;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t4 || $[9] !== t5) {
    t6 = {
      transition: t4,
      paper: t5
    };
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== header) {
    t7 = header ? {
      ...header
    } : undefined;
    $[11] = header;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== footer) {
    t8 = footer ? {
      ...footer
    } : undefined;
    $[13] = footer;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== scrollableBody) {
    t9 = scrollableBody ? {
      minHeight: 0
    } : undefined;
    $[15] = scrollableBody;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const t10 = !disableGutters;
  let t11;
  if ($[17] !== t10) {
    t11 = classnames("h-full", {
      "py-[24px]": t10
    });
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== isLoading) {
    t12 = isLoading && <Loader />;
    $[19] = isLoading;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== isLoading || $[22] !== props.children) {
    t13 = !isLoading && <>{props.children}</>;
    $[21] = isLoading;
    $[22] = props.children;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t12 || $[26] !== t13) {
    t14 = <Stack className={t11}>{t12}{t13}</Stack>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t15 = <PanelWrapper rounded={true} header={t7} footer={t8} sx={t9}>{t14}</PanelWrapper>;
    $[28] = t14;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== fullScreen || $[34] !== isOpen || $[35] !== t15 || $[36] !== t3 || $[37] !== t6 || $[38] !== transitionDuration) {
    t16 = <Dialog fullScreen={fullScreen} transitionDuration={transitionDuration} onClose={t3} onClick={_temp} open={isOpen} slotProps={t6}>{t15}</Dialog>;
    $[33] = fullScreen;
    $[34] = isOpen;
    $[35] = t15;
    $[36] = t3;
    $[37] = t6;
    $[38] = transitionDuration;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  return t16;
}
function _temp(event) {
  return event.stopPropagation();
}